import {
  getSemverVersionFromProductVersion,
  type ProductSemverVersionsByProduct,
} from '@orus.eu/activity-configuration'
import {
  type AbstractDimension,
  type BooleanDimension,
  esMrpaxeSelectedDimension,
  esMrpwSelectedDimension,
  esRcphSelectedDimension,
  frAupaSelectedDimension,
  frMrgaSelectedDimension,
  frMrpaxeSelectedDimension,
  frPrelSelectedDimension,
  frRcpaxeSelectedDimension,
  frRcpwSelectedDimension,
  mrpSelectedDimension,
  mrpaSelectedDimension,
  mrphSelectedDimension,
  mrpwSelectedDimension,
  mutaSelectedDimension,
  nlAovqlSelectedDimension,
  nlRcphSelectedDimension,
  nomenclatureVersionDimension,
  type PartialDimensionnedState,
  type ProductDimensionsModule,
  type ProductMigratedQuoteV3Name,
  type ProductMigratedQuoteV3ResultName,
  type ProductQuoteName,
  type ProductWithQuoteV2,
  frRccwSelectedDimension,
  rcdaSelectedDimension,
  rcpaSelectedDimension,
  rcpaxaSelectedDimension,
  rcphSelectedDimension,
  type StringsEnumDimension,
  type TypeOfDimension,
} from '@orus.eu/dimensions'
import { esMrpaxeProductVersionDimension, esMrpaxeQuoteDimension } from '@orus.eu/dimensions-es-mrpaxe'
import { esMrpwProductVersionDimension, esMrpwQuoteDimension } from '@orus.eu/dimensions-es-mrpw'
import {
  esRcphProductDimension,
  esRcphProductVersionDimension,
  esRcphQuoteDimension,
  esRcphQuoteV3Dimension,
} from '@orus.eu/dimensions-es-rcph'
import { frAupaProductVersionDimension, frAupaQuoteDimension } from '@orus.eu/dimensions-fr-aupa'
import { frMrgaProductVersionDimension, frMrgaQuoteDimension } from '@orus.eu/dimensions-fr-mrga'
import { frMrpaxeProductVersionDimension, frMrpaxeQuoteDimension } from '@orus.eu/dimensions-fr-mrpaxe'
import { frPrelProductVersionDimension, frPrelQuoteDimension } from '@orus.eu/dimensions-fr-prel'
import { frRccwProductVersionDimension, frRccwQuoteDimension } from '@orus.eu/dimensions-fr-rccw'
import { frRcpSelectedDimension } from '@orus.eu/dimensions-fr-rcp'
import { frRcpaxeProductVersionDimension, frRcpaxeQuoteDimension } from '@orus.eu/dimensions-fr-rcpaxe'
import { frRcpwProductVersionDimension, frRcpwQuoteDimension } from '@orus.eu/dimensions-fr-rcpw'
import { mrpaProductVersionDimension, mrpaQuoteDimension } from '@orus.eu/dimensions-mrpa'
import { mrphProductVersionDimension, mrphQuoteV2Dimension } from '@orus.eu/dimensions-mrph'
import { mrpwQuoteDimension, mrpwQuoteV3Dimension, mrpwVersionDimension } from '@orus.eu/dimensions-mrpw'
import { mutaProductVersionDimension, mutaQuoteDimension } from '@orus.eu/dimensions-muta'
import { nlAovqlProductVersionDimension, nlAovqlQuoteDimension } from '@orus.eu/dimensions-nl-aovql'
import { nlRcphProductVersionDimension, nlRcphQuoteDimension } from '@orus.eu/dimensions-nl-rcph'
import { rcdaQuoteDimension } from '@orus.eu/dimensions-rcda'
import { rcpaProductVersionDimension, rcpaQuoteDimension } from '@orus.eu/dimensions-rcpa'
import { rcpaxaProductVersionDimension, rcpaxaQuoteDimension } from '@orus.eu/dimensions-rcpaxa'
import {
  rcphOptionCyberDimension,
  rcphOptionMrpSmallOfficeDimension,
  rcphOptionTdmiDimension,
  rcphOptionTrmDimension,
  rcphQuoteV2Dimension,
  rcphQuoteV3Dimension,
  rcphVersionDimension,
} from '@orus.eu/dimensions-rcph'
import { assertDefinedAndNotNull } from '@orus.eu/error'
import {
  allProductFamilies,
  allProducts,
  type MrpProduct,
  mrpProducts,
  type Product,
  type ProductFamily,
  type ProductsPerProductFamily,
  type ProductVersionForProduct,
  type ProductVersionsByProduct,
  type VersionedProduct,
} from '@orus.eu/product'
import type { UnwrapResult } from '@orus.eu/result'
import { objectEntries, objectValues } from '@orus.eu/utils'

import { productDimensionsModules } from './product-modules.ts'

// @orus.eu/keep-sorted
export const productVersionsByProduct = {
  'es-mrpaxe': esMrpaxeProductVersionDimension,
  'es-mrpw': esMrpwProductVersionDimension,
  'fr-aupa': frAupaProductVersionDimension,
  'fr-mrga': frMrgaProductVersionDimension,
  'fr-mrpaxe': frMrpaxeProductVersionDimension,
  'fr-prel': frPrelProductVersionDimension,
  'fr-rccw': frRccwProductVersionDimension,
  'fr-rcpaxe': frRcpaxeProductVersionDimension,
  'fr-rcpw': frRcpwProductVersionDimension,
  mrpa: mrpaProductVersionDimension,
  'nl-aovql': nlAovqlProductVersionDimension,
  'nl-rcph': nlRcphProductVersionDimension,
} as const satisfies Record<VersionedProduct, StringsEnumDimension<string, string[]>>

export const productSelectionDimensionsByProduct = {
  mrph: mrphSelectedDimension,
  mrpw: mrpwSelectedDimension,
  rcph: rcphSelectedDimension,
  muta: mutaSelectedDimension,
  rcda: rcdaSelectedDimension,
  'es-rcph': esRcphSelectedDimension,
  'fr-prel': frPrelSelectedDimension,
  'fr-rcpw': frRcpwSelectedDimension,
  'nl-rcph': nlRcphSelectedDimension,
  rcpa: rcpaSelectedDimension,
  mrpa: mrpaSelectedDimension,
  rcpaxa: rcpaxaSelectedDimension,
  'es-mrpw': esMrpwSelectedDimension,
  'fr-rccw': frRccwSelectedDimension,
  'nl-aovql': nlAovqlSelectedDimension,
  'fr-aupa': frAupaSelectedDimension,
  'fr-mrga': frMrgaSelectedDimension,
  'fr-mrpaxe': frMrpaxeSelectedDimension,
  'fr-rcpaxe': frRcpaxeSelectedDimension,
  'es-mrpaxe': esMrpaxeSelectedDimension,
} as const satisfies Record<Product, BooleanDimension<`${string}Selected`>>
export const productSelectionDimensions = objectValues(productSelectionDimensionsByProduct)

export type SelectedProducts = PartialDimensionnedState<typeof productSelectionDimensions>

export function isProductSelected(
  state: PartialDimensionnedState<typeof productSelectionDimensions>,
  product: Product,
): boolean {
  return !!state[productSelectionDimensionsByProduct[product].name]
}

export function getSelectedProducts(state: PartialDimensionnedState<typeof productSelectionDimensions>): Product[] {
  return allProducts.filter((product) => isProductSelected(state, product))
}

export const productVersionDimensionsByProduct = {
  'es-mrpw': esMrpwProductVersionDimension,
  'es-rcph': esRcphProductVersionDimension,
  'fr-prel': frPrelProductVersionDimension,
  'fr-rcpw': frRcpwProductVersionDimension,
  'nl-rcph': nlRcphProductVersionDimension,
  mrpa: mrpaProductVersionDimension,
  mrph: mrphProductVersionDimension,
  mrpw: mrpwVersionDimension,
  muta: mutaProductVersionDimension,
  rcda: nomenclatureVersionDimension,
  rcpa: rcpaProductVersionDimension,
  rcpaxa: rcpaxaProductVersionDimension,
  rcph: rcphVersionDimension,
  'fr-rccw': frRccwProductVersionDimension,
  'nl-aovql': nlAovqlProductVersionDimension,
  'fr-aupa': frAupaProductVersionDimension,
  'fr-mrga': frMrgaProductVersionDimension,
  'fr-mrpaxe': frMrpaxeProductVersionDimension,
  'fr-rcpaxe': frRcpaxeProductVersionDimension,
  'es-mrpaxe': esMrpaxeProductVersionDimension,
} as const satisfies Record<Product, AbstractDimension>
export const productVersionDimensions = objectValues(productVersionDimensionsByProduct)

/**
 * Build a `ProductVersionsByProduct` object from the provided state.
 */
export function getProductVersionsByProduct(
  state: PartialDimensionnedState<typeof productVersionDimensions>,
): Partial<ProductVersionsByProduct> {
  return objectEntries(productVersionDimensionsByProduct).reduce((acc, [product, productVersionDimension]) => {
    const productVersion = state[productVersionDimension.name]
    if (productVersion) {
      setProductVersionForProduct(acc, product, productVersion)
    }
    return acc
  }, {})
}
function setProductVersionForProduct<const P extends Product>(
  productVersionsByProduct: Partial<ProductVersionsByProduct>,
  product: P,
  productVersion: ProductVersionForProduct<P>,
) {
  productVersionsByProduct[product] = productVersion
}

/**
 * Resolves the semver version of each product present on the subscription state.
 * Used in places like `productSemverRange` so its condition can grandfather
 * older-version subscriptions. Products whose versions are unset are omitted
 * (a `productSemverRange` clause can't match without a version).
 */
export function getProductSemverVersionsByProduct(
  state: PartialDimensionnedState<typeof productVersionDimensions>,
): Partial<ProductSemverVersionsByProduct> {
  return objectEntries(productVersionDimensionsByProduct).reduce<Partial<ProductSemverVersionsByProduct>>(
    (acc, [product, productVersionDimension]) => {
      const productVersion = state[productVersionDimension.name]
      if (productVersion) {
        acc[product] = getSemverVersionFromProductVersion(product, productVersion)
      }

      return acc
    },
    {},
  )
}

export const productFamilySelectionDimensionsByProductFamily = {
  'fr-rcp': frRcpSelectedDimension,
  mrp: mrpSelectedDimension,
} as const satisfies Record<ProductFamily, BooleanDimension<`${string}Selected`>>
export const productFamilySelectionDimensions = objectValues(productFamilySelectionDimensionsByProductFamily)

export function isProductFamilySelected(
  state: PartialDimensionnedState<typeof productFamilySelectionDimensions>,
  productFamily: ProductFamily,
): boolean {
  return !!state[productFamilySelectionDimensionsByProductFamily[productFamily].name]
}

export function getSelectedProductFamilies(
  state: PartialDimensionnedState<typeof productFamilySelectionDimensions>,
): ProductFamily[] {
  return allProductFamilies.filter((productFamily) => isProductFamilySelected(state, productFamily))
}

export const productSelectionDimensionsByFamily = {
  'fr-rcp': {
    'fr-rcpw': frRcpwSelectedDimension,
    rcph: rcphSelectedDimension,
    'fr-rcpaxe': frRcpaxeSelectedDimension,
  },
  mrp: {
    mrpw: mrpwSelectedDimension,
    mrpa: mrpaSelectedDimension,
    mrph: mrphSelectedDimension,
    'es-mrpw': esMrpwSelectedDimension,
    'fr-mrpaxe': frMrpaxeSelectedDimension,
    'es-mrpaxe': esMrpaxeSelectedDimension,
  },
} as const satisfies {
  [Family in keyof ProductsPerProductFamily]: {
    [P in ProductsPerProductFamily[Family][number]]: BooleanDimension<`${string}Selected`>
  }
}
export const frRcpProductSelectionDimensions = objectValues(productSelectionDimensionsByFamily['fr-rcp'])
export const mrpProductSelectionDimensions = objectValues(productSelectionDimensionsByFamily['mrp'])
export const mrpProductSelectionDimensionsByProduct = productSelectionDimensionsByFamily['mrp']

export function getSelectedMrpProducts(
  state: PartialDimensionnedState<typeof productSelectionDimensions>,
): MrpProduct[] {
  return mrpProducts.filter((product) => isProductSelected(state, product))
}

export const activableProductInformationIdSelectionDimensions = [
  ...productSelectionDimensions,
  esRcphProductDimension,
  rcphOptionCyberDimension,
  rcphOptionTdmiDimension,
  rcphOptionMrpSmallOfficeDimension,
  rcphOptionTrmDimension,
] as const

// TODO: review this
export type QuoteTypeByProduct<P extends Product> =
  | TypeOfDimension<(typeof _productQuoteDimension)[P]>
  | (P extends keyof typeof _productMigratedQuoteV3Dimension
      ? TypeOfDimension<(typeof _productMigratedQuoteV3Dimension)[P]>
      : never)

const _productMigratedQuoteV3Dimension = {
  'es-rcph': esRcphQuoteV3Dimension,
  rcph: rcphQuoteV3Dimension,
  mrpw: mrpwQuoteV3Dimension,
} as const satisfies Partial<Record<Product, AbstractDimension>>

const _productQuoteDimension = {
  mrph: mrphQuoteV2Dimension,
  mrpw: mrpwQuoteDimension,
  muta: mutaQuoteDimension,
  rcda: rcdaQuoteDimension,
  rcph: rcphQuoteV2Dimension,
  'es-rcph': esRcphQuoteDimension,
  rcpa: rcpaQuoteDimension,
  mrpa: mrpaQuoteDimension,
  'fr-prel': frPrelQuoteDimension,
  'fr-rcpw': frRcpwQuoteDimension,
  'nl-rcph': nlRcphQuoteDimension,
  rcpaxa: rcpaxaQuoteDimension,
  'es-mrpw': esMrpwQuoteDimension,
  'fr-rccw': frRccwQuoteDimension,
  'nl-aovql': nlAovqlQuoteDimension,
  'fr-aupa': frAupaQuoteDimension,
  'fr-mrga': frMrgaQuoteDimension,
  'fr-mrpaxe': frMrpaxeQuoteDimension,
  'fr-rcpaxe': frRcpaxeQuoteDimension,
  'es-mrpaxe': esMrpaxeQuoteDimension,
} as const satisfies Record<Product, AbstractDimension>

export type DatapipelineQuotesV3 = {
  [P in keyof typeof productQuoteV3Dimension]: UnwrapResult<TypeOfDimension<(typeof productQuoteV3Dimension)[P]>>
}[keyof typeof productQuoteV3Dimension]

export type QuotesV2 = TypeOfDimension<(typeof productQuoteV2Dimension)[ProductWithQuoteV2]>

/**
 * Precise per-product record derived by folding the module list: each product whose module carries a `quoteV3Dimension`
 * slot maps to that slot's concrete dimension type. Keeping the per-product value types precise (rather than collapsing
 * them to a single `AbstractDimension` union) is what lets {@link DatapipelineQuotesV3} unwrap each product's own quote
 * result type — a uniform value type would defeat `UnwrapResult`'s per-member unwrapping.
 */
type QuoteV3DimensionByProduct<Modules extends readonly ProductDimensionsModule[]> = {
  [
    P in Modules[number]['product'] as Extract<Modules[number], { product: P }> extends {
      quoteV3Dimension: AbstractDimension
    }
      ? P
      : never
  ]: Extract<Modules[number], { product: P }> extends { quoteV3Dimension: infer Q extends AbstractDimension }
    ? Q
    : never
}

function buildQuoteV3DimensionByProduct<const Modules extends readonly ProductDimensionsModule[]>(
  modules: Modules,
): QuoteV3DimensionByProduct<Modules>
function buildQuoteV3DimensionByProduct(
  modules: readonly ProductDimensionsModule[],
): Partial<Record<Product, AbstractDimension>> {
  const record: Partial<Record<Product, AbstractDimension>> = {}
  for (const module of modules) {
    if (module.quoteV3Dimension !== undefined) {
      record[module.product] = module.quoteV3Dimension
    }
  }
  return record
}

export const productQuoteV3Dimension = buildQuoteV3DimensionByProduct(productDimensionsModules)

export const allQuotesV3Types = [...Object.values(productQuoteV3Dimension).map((dimension) => dimension.name)] as const
export type AllQuoteV3TypesNames = (typeof allQuotesV3Types)[number]

export const subscriptionHasQuoteV3 = (input: Record<string, unknown>): boolean => {
  return objectEntries(productQuoteV3Dimension).some(([product, quoteDimension]) => {
    const quote = input[quoteDimension.name]
    return !!quote && !!input[productSelectionDimensionsByProduct[product].name]
  })
}

export const productQuoteV2Dimension = {
  mrph: mrphQuoteV2Dimension,
  muta: mutaQuoteDimension,
  rcda: rcdaQuoteDimension,
  rcph: rcphQuoteV2Dimension,
  'es-rcph': esRcphQuoteDimension,
} as const satisfies Record<ProductWithQuoteV2, AbstractDimension>

export const productQuoteDimensionNames = {
  mrph: mrphQuoteV2Dimension.name,
  mrpw: { migratedToV3: { type: 'result', quoteName: 'mrpwQuoteV3' }, v2: mrpwQuoteDimension.name },
  muta: mutaQuoteDimension.name,
  rcda: rcdaQuoteDimension.name,
  rcph: { migratedToV3: { type: 'quote', quoteName: 'rcphQuoteV3' }, v2: rcphQuoteV2Dimension.name },
  'es-rcph': { migratedToV3: { type: 'quote', quoteName: 'esRcphQuoteV3' }, v2: esRcphQuoteDimension.name },
  rcpa: rcpaQuoteDimension.name,
  mrpa: mrpaQuoteDimension.name,
  'fr-prel': frPrelQuoteDimension.name,
  'fr-rcpw': frRcpwQuoteDimension.name,
  'nl-rcph': nlRcphQuoteDimension.name,
  rcpaxa: rcpaxaQuoteDimension.name,
  'es-mrpw': esMrpwQuoteDimension.name,
  'fr-rccw': frRccwQuoteDimension.name,
  'nl-aovql': nlAovqlQuoteDimension.name,
  'fr-aupa': frAupaQuoteDimension.name,
  'fr-mrga': frMrgaQuoteDimension.name,
  'fr-mrpaxe': frMrpaxeQuoteDimension.name,
  'fr-rcpaxe': frRcpaxeQuoteDimension.name,
  'es-mrpaxe': esMrpaxeQuoteDimension.name,
} as const satisfies Record<
  Product,
  | ProductQuoteName
  | {
      migratedToV3:
        | { type: 'quote'; quoteName: ProductMigratedQuoteV3Name }
        | { type: 'result'; quoteName: ProductMigratedQuoteV3ResultName }
      v2: ProductQuoteName
    }
>

// @orus.eu/keep-sorted
export const allRiskCarriers = [
  'acheel',
  'aig',
  'axa',
  'axeria',
  'hiscox',
  'lourmel',
  'quantum-leben',
  'wakam',
] as const
export type RiskCarrier = (typeof allRiskCarriers)[number]

// @orus.eu/keep-sorted
const RISK_CARRIER_PER_PRODUCT = {
  'es-mrpaxe': 'axeria',
  'es-mrpw': 'wakam',
  'es-rcph': 'hiscox',
  'fr-aupa': 'axa',
  'fr-mrga': 'axeria',
  'fr-mrpaxe': 'axeria',
  'fr-prel': 'lourmel',
  'fr-rccw': 'wakam',
  'fr-rcpaxe': 'axeria',
  'fr-rcpw': 'wakam',
  mrpa: 'aig',
  mrph: 'hiscox',
  mrpw: 'wakam',
  muta: 'acheel',
  'nl-aovql': 'quantum-leben',
  'nl-rcph': 'hiscox',
  rcda: 'axeria',
  rcpa: 'aig',
  rcpaxa: 'axa',
  rcph: 'hiscox',
} as const satisfies Record<Product, RiskCarrier>

export type ProductRiskCarrier<P extends Product> = (typeof RISK_CARRIER_PER_PRODUCT)[P]

export function getProductRiskCarrier<P extends Product>(product: P): ProductRiskCarrier<P> {
  return RISK_CARRIER_PER_PRODUCT[product]
}

export function getProductsRiskCarriers(products: Product[]): RiskCarrier[] {
  return [...new Set(products.map((product) => getProductRiskCarrier(product)))]
}

// @orus.eu/keep-sorted
const formattedRiskCarrierName: Record<RiskCarrier, string> = {
  acheel: 'Acheel',
  aig: 'AIG',
  axa: 'Axa',
  axeria: 'Axeria',
  hiscox: 'Hiscox',
  lourmel: 'Lourmel',
  'quantum-leben': 'Quantum Leben',
  wakam: 'Wakam',
}

export function getProductRiskCarrierForDisplay(product: Product): string {
  const riskCarrier = getProductRiskCarrier(product)
  return assertDefinedAndNotNull(formattedRiskCarrierName[riskCarrier], 'Cannot get risk-carrier name for display', {
    product,
    riskCarrier,
  })
}
